/** A point in pixels, e.g. from `map.project()` (Leaflet's `L.Point` is one) */
export type PixelPoint = { x: number; y: number };

const squaredDistance = (a: PixelPoint, b: PixelPoint) =>
  (a.x - b.x) ** 2 + (a.y - b.y) ** 2;

/** Squared distance of the point from the segment a-b */
const squaredDistanceToSegment = (
  point: PixelPoint,
  a: PixelPoint,
  b: PixelPoint
) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return squaredDistance(point, a);

  // Where the point projects onto the segment: 0 at a, 1 at b
  const t = Math.max(
    0,
    Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared)
  );
  return squaredDistance(point, { x: a.x + t * dx, y: a.y + t * dy });
};

/**
 * Index of the track point under the cursor, null when the cursor isn't on the track line.
 * The cursor is on the line when it's within `tolerance` pixels of one of its segments. Of the nearest
 * segment the point closer to the cursor is returned, since the chart has a value only at the track's points.
 */
export const findHoveredTrackpoint = (
  points: PixelPoint[],
  cursor: PixelPoint,
  tolerance: number
): number | null => {
  const toleranceSquared = tolerance * tolerance;

  if (points.length === 1) {
    return squaredDistance(cursor, points[0]) <= toleranceSquared ? 0 : null;
  }

  let nearestSegment = -1;
  let nearestDistance = Infinity;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    // Segments whose bounding box is farther than the tolerance can't be nearer, skipped cheaply
    if (
      cursor.x < Math.min(a.x, b.x) - tolerance ||
      cursor.x > Math.max(a.x, b.x) + tolerance ||
      cursor.y < Math.min(a.y, b.y) - tolerance ||
      cursor.y > Math.max(a.y, b.y) + tolerance
    ) {
      continue;
    }
    const distance = squaredDistanceToSegment(cursor, a, b);
    // Of overlapping segments (a track going back the same way) the first one wins
    if (distance <= toleranceSquared && distance < nearestDistance) {
      nearestDistance = distance;
      nearestSegment = i;
    }
  }
  if (nearestSegment === -1) return null;

  const start = nearestSegment - 1;
  return squaredDistance(cursor, points[start]) <=
    squaredDistance(cursor, points[nearestSegment])
    ? start
    : nearestSegment;
};
